"use client";

import AutorenewIcon from "@mui/icons-material/Autorenew";
import CameraEnhanceIcon from "@mui/icons-material/CameraEnhance";
import CloseIcon from "@mui/icons-material/Close";
import FlipCameraAndroidIcon from "@mui/icons-material/FlipCameraAndroid";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import RefreshIcon from "@mui/icons-material/Refresh";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Button,
  CircularProgress,
  Dialog,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Webcam from "react-webcam";
import { processImage } from "../Utils/ProcessImages";
import { Vocabulary } from "../Utils/Vocabulary";
import styles from "./ScanIdModal.module.css";
import { ScanDocumentType } from "../Utils/Constants";
import { isMobile, isTablet } from "../Utils/Utils";

interface ScanIdProps {
  openModal: boolean;
  handleClose: () => void;
  doneProcess: (data: any) => void;
  documentType?: string;
}

type CameraSideType = "user" | "environment";

export default function ScanId(props: ScanIdProps) {
  const {
    openModal,
    handleClose,
    doneProcess,
    documentType = ScanDocumentType.ID,
  } = props;
  const [image, setImage] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [frontData, setFrontData] = useState<any>(null);
  const [isProcessingBack, setIsProcessingBack] = useState(false);
  const [isNewIdModel, setIsNewIdModel] = useState(false);
  const webcamRef = useRef<Webcam>(null);
  const [showCamera, setShowCamera] = useState(true);
  const [cameraSide, setCameraSide] = useState<CameraSideType>(() =>
    isMobile() || isTablet() ? "environment" : "user"
  );
  const [backCameras, setBackCameras] = useState<MediaDeviceInfo[]>([]);
  const [currentBackCameraIndex, setCurrentBackCameraIndex] =
    useState<number>(0);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | undefined>(
    undefined
  );
  const isMobileDevice = isMobile() || isTablet();
  const notifyError = (message: string) => toast.error(message);
  const notifySuccess = (message: string) => toast.success(message);

  const enumerateCameras = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter((device) => device.kind === "videoinput");
      const backCams = videoDevices.filter(
        (device) =>
          device.label.toLowerCase().includes("back") ||
          device.label.toLowerCase().includes("rear") ||
          device.label.toLowerCase().includes("environment") ||
          (isMobileDevice && device.label !== "" && !device.label.toLowerCase().includes("front"))
      );
      setBackCameras(backCams);
    } catch (error) {
      console.warn("Could not enumerate cameras:", error);
    }
  }, [isMobileDevice]);

  // const ROMANIAN_ID_ASPECT_RATIO = 1.58;
  const ROMANIAN_ID_ASPECT_RATIO = 1.5;
  const LANDSCAPE_ID_ASPECT_RATIO = 0.67; // Inverted for landscape orientation

  // Calculate rectangle based on device orientation
  const isLandscapeMode = isMobileDevice && showCamera;

  const rectangle = isLandscapeMode
    ? {
        width: 0.85,
        height: 0.85 * LANDSCAPE_ID_ASPECT_RATIO,
        x: 0.075,
        y: (1 - 0.85 * LANDSCAPE_ID_ASPECT_RATIO) / 2,
      }
    : {
        width: 0.75,
        height: 0.85 / ROMANIAN_ID_ASPECT_RATIO,
        x: 0.125,
        y: (1 - 0.85 / ROMANIAN_ID_ASPECT_RATIO) / 2,
      };

  // Handle screen orientation locking for mobile devices
  useEffect(() => {
    const lockOrientation = async () => {
      if (
        openModal &&
        showCamera &&
        isMobileDevice &&
        typeof window !== "undefined"
      ) {
        try {
          // Check if Screen Orientation API is supported
          // eslint-disable-next-line no-restricted-globals
          const screenOrientation = screen.orientation;
          if (
            screenOrientation &&
            typeof (screenOrientation as any).lock === "function"
          ) {
            await (screenOrientation as any).lock("landscape-primary");
          }
        } catch (error) {
          console.warn("Could not lock screen orientation:", error);
          // Gracefully handle browsers that don't support orientation locking
        }
      }
    };

    const unlockOrientation = () => {
      try {
        // eslint-disable-next-line no-restricted-globals
        const screenOrientation = screen.orientation;
        if (
          screenOrientation &&
          typeof (screenOrientation as any).unlock === "function"
        ) {
          (screenOrientation as any).unlock();
        }
      } catch (error) {
        console.warn("Could not unlock screen orientation:", error);
      }
    };

    if (openModal && showCamera && isMobileDevice) {
      lockOrientation();
    } else if (!showCamera || !openModal) {
      unlockOrientation();
    }

    // Cleanup on unmount
    return () => {
      unlockOrientation();
    };
  }, [openModal, showCamera]);

  // Updated takePicture with better focus trigger
  const takePicture = async () => {
    if (isMobileDevice) {
      // Trigger focus adjustment
      await handleTapToFocus();
      // Longer delay for Samsung devices to adjust focus
      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc && webcamRef.current) {
      const webcamElement = webcamRef.current.video;
      const containerInfo = {
        width: webcamElement?.clientWidth || 600,
        height: webcamElement?.clientHeight || (isMobileDevice ? 320 : 450),
      };
      const croppedImage = await cropCameraImage(
        imageSrc,
        rectangle,
        containerInfo
      );
      setImage(croppedImage);
      setShowCamera(false);
    }
  };

  const cropCameraImage = (imageSrc: any, rect: any, containerInfo: any) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = imageSrc;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        const actualWidth = img.width;
        const actualHeight = img.height;
        const containerWidth = containerInfo.width;
        const containerHeight = containerInfo.height;

        const imageAspectRatio = actualWidth / actualHeight;
        const containerAspectRatio = containerWidth / containerHeight;

        let scaledWidth,
          scaledHeight,
          offsetX = 0,
          offsetY = 0;

        if (imageAspectRatio > containerAspectRatio) {
          // Image is wider - height fills container, width overflows
          scaledHeight = actualHeight;
          scaledWidth = actualHeight * containerAspectRatio;
          offsetX = (actualWidth - scaledWidth) / 2;
        } else {
          // Image is taller - width fills container, height overflows
          scaledWidth = actualWidth;
          scaledHeight = actualWidth / containerAspectRatio;
          offsetY = (actualHeight - scaledHeight) / 2;
        }

        // Calculate crop coordinates based on the visible area
        const cropX = offsetX + rect.x * scaledWidth;
        const cropY = offsetY + rect.y * scaledHeight;
        const cropWidth = rect.width * scaledWidth;
        const cropHeight = rect.height * scaledHeight;

        // Set canvas size to match crop dimensions
        canvas.width = cropWidth;
        canvas.height = cropHeight;
        if (!ctx) return;
        ctx.drawImage(
          img,
          cropX,
          cropY,
          cropWidth,
          cropHeight,
          0,
          0,
          cropWidth,
          cropHeight
        );

        resolve(canvas.toDataURL("image/jpeg", 1.0)); // Maximum quality
      };
    });
  };

  const handleFileUpload = (event: any) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (!e.target) return;
        const uploadedImage = e.target.result;
        setImage(uploadedImage);
        setShowCamera(false);
      };
      reader.readAsDataURL(file);
    }
  };
  const videoConstraints = {
    width: { ideal: isMobileDevice ? 1920 : 1280 },
    height: { ideal: isMobileDevice ? 1080 : 720 },
    // Use specific deviceId for back cameras on mobile, otherwise use facingMode
    ...(selectedDeviceId && cameraSide === "environment"
      ? { deviceId: { exact: selectedDeviceId } }
      : { facingMode: cameraSide }),
    aspectRatio: isMobileDevice ? 16 / 9 : 4 / 3,
    // Remove the advanced constraints from here - we'll apply them after camera loads
  } as any;

  const handleUserMedia = useCallback(() => {
    setCameraReady(true);
    setCameraError(null);

    // Enumerate cameras after permission is granted so labels and deviceIds are real
    if (isMobileDevice) enumerateCameras();
  }, [enumerateCameras, isMobileDevice]);

  const handleUserMediaError = useCallback((error: any) => {
    console.error("Camera error:", error);
    setCameraReady(false);

    if (
      error.name === "NotAllowedError" ||
      error.name === "PermissionDeniedError"
    ) {
      setCameraError(Vocabulary.cameraPermissionDenied);
    } else if (
      error.name === "NotFoundError" ||
      error.name === "DevicesNotFoundError"
    ) {
      setCameraError(Vocabulary.cameraNotFound);
    } else {
      setCameraError(`Camera error: ${error.message || "Unknown error"}`);
    }
  }, []);

  // Cycle through back cameras
  const cycleBackCamera = useCallback(() => {
    if (backCameras.length <= 1) return;

    const nextIndex = (currentBackCameraIndex + 1) % backCameras.length;
    setCurrentBackCameraIndex(nextIndex);
    setSelectedDeviceId(backCameras[nextIndex].deviceId);

    // Reset camera ready state to reinitialize with new camera
    setCameraReady(false);
  }, [backCameras, currentBackCameraIndex]);

  // Tap to focus functionality for mobile devices - focuses on NEAR objects
  const handleTapToFocus = useCallback(async () => {
    if (!isMobileDevice || !webcamRef.current?.video) return;

    try {
      const videoTrack = webcamRef.current.video.srcObject as MediaStream;
      const track = videoTrack?.getVideoTracks()[0];

      if (track && "getCapabilities" in track) {
        const capabilities = track.getCapabilities() as any;
        const constraints: any = { advanced: [] };

        console.log("Camera capabilities:", capabilities); // Debug log

        // Priority 1: Try continuous autofocus (works best on most Android devices)
        if (capabilities.focusMode?.includes("continuous")) {
          constraints.advanced.push({ focusMode: "continuous" });
        } else if (capabilities.focusMode?.includes("single-shot")) {
          constraints.advanced.push({ focusMode: "single-shot" });
        }

        // Priority 2: Set focus distance for close objects
        if (capabilities.focusDistance) {
          const { min = 0, max = 1 } = capabilities.focusDistance;

          // For ID scanning, we want focus at ~20-30cm
          // Most devices use 0-1 range where higher = closer
          // Samsung devices often need values between 0.15-0.35 for close focus
          const closeDistance = min + (max - min) * 0.3;

          constraints.advanced.push({
            focusDistance: { ideal: closeDistance, min: min, max: max },
          });
        }

        // Apply constraints
        await track.applyConstraints(constraints);

        // Give camera time to adjust
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
    } catch (error) {
      console.warn("Focus adjustment failed:", error);
    }
  }, []);

  // Automatically set close focus when camera is ready
  // Improved camera ready handler with multiple focus attempts
  useEffect(() => {
    if (cameraReady && isMobileDevice) {
      let attempts = 0;
      const maxAttempts = 3;

      const applyFocus = async () => {
        attempts++;
        await handleTapToFocus();

        // Retry if we haven't reached max attempts
        if (attempts < maxAttempts) {
          setTimeout(applyFocus, 1000);
        }
      };

      // Initial delay to ensure camera is fully initialized
      const timer = setTimeout(() => {
        applyFocus();
      }, 800);

      return () => clearTimeout(timer);
    }
  }, [cameraReady, cameraSide, handleTapToFocus]); // Add cameraSide to deps

  // Reset focus when camera side changes
  useEffect(() => {
    if (cameraReady) {
      setCameraReady(false);
      // Camera will re-initialize and trigger the focus setup again
      setTimeout(() => {
        // The onUserMedia callback will set cameraReady to true again
      }, 100);
    }
  }, [cameraSide]);

  const handleProcessImage = async (image: string) => {
    setIsModalOpen(true);
    try {
      if (documentType === ScanDocumentType.DRIVER_LICENSE) {
        if (!frontData) {
          const frontResult = await processImage(image, ScanDocumentType.DRIVER_LICENSE);
          if (frontResult && frontResult.code === 200) {
            setFrontData(frontResult);
            setIsProcessingBack(true);
            setImage(null);
            setShowCamera(true);
            setIsModalOpen(false);
            notifySuccess(Vocabulary.frontSideProcessedSuccessfully);
          } else {
            throw new Error("Front processing failed");
          }
        } else {
          const backResult = await processImage(image, ScanDocumentType.DRIVER_LICENSE_BACK);
          if (backResult && backResult.code === 200) {
            const combinedResult = {
              status: frontData.status,
              code: frontData.code,
              result: { ...frontData.result, ...backResult.result },
              image: [...(frontData.image || []), ...(backResult.image || [])],
            };
            setIsModalOpen(false);
            doneProcess(combinedResult);
            setImage(null);
            setShowCamera(true);
            setFrontData(null);
            setIsProcessingBack(false);
            notifySuccess(Vocabulary.imageProcessedSuccessfully);
          } else {
            throw new Error("Back processing failed");
          }
        }
      } else if (documentType === ScanDocumentType.ID && isNewIdModel) {
        if (!frontData) {
          const frontResult = await processImage(image, ScanDocumentType.ID);
          if (frontResult && frontResult.code === 200) {
            setFrontData(frontResult);
            setIsProcessingBack(true);
            setImage(null);
            setShowCamera(true);
            setIsModalOpen(false);
            notifySuccess(Vocabulary.idFrontSideProcessedSuccessfully);
          } else {
            throw new Error("Front processing failed");
          }
        } else {
          const backResult = await processImage(image, ScanDocumentType.ID_BACK);
          if (backResult && backResult.code === 200) {
            const combinedResult = {
              status: frontData.status,
              code: frontData.code,
              result: { ...frontData.result, ...backResult.result },
              image: [...(frontData.image || []), ...(backResult.image || [])],
            };
            setIsModalOpen(false);
            doneProcess(combinedResult);
            setImage(null);
            setShowCamera(true);
            setFrontData(null);
            setIsProcessingBack(false);
            notifySuccess(Vocabulary.imageProcessedSuccessfully);
          } else {
            throw new Error("Back processing failed");
          }
        }
      } else {
        const result = await processImage(image, documentType);
        if (result && result.code === 200) {
          setIsModalOpen(false);
          doneProcess(result);
          setImage(null);
          setShowCamera(true);
          notifySuccess(Vocabulary.imageProcessedSuccessfully);
        } else {
          throw new Error("Processing failed");
        }
      }
    } catch (error) {
      setIsModalOpen(false);
      console.error("Error processing image:", error);
      notifyError(Vocabulary.imageProcessingFailed);
    }
  };

  const resetCamera = () => {
    setImage(null);
    setShowCamera(true);
  };

  const handleModalClose = () => {
    setFrontData(null);
    setIsProcessingBack(false);
    setIsNewIdModel(false);
    resetCamera();
    handleClose();
  };

  return (
    <>
      <Dialog
        open={openModal}
        onClose={handleModalClose}
        maxWidth={isMobileDevice ? false : "md"}
        fullWidth={!isMobileDevice}
        fullScreen={isMobileDevice}
        PaperProps={{
          sx: {
            borderRadius: isMobileDevice ? 0 : "16px",
            maxHeight: isMobileDevice ? "100vh" : "90vh",
            margin: isMobileDevice ? 0 : { xs: "16px", sm: "32px" },
            height: isMobileDevice ? "100vh" : "auto",
          },
        }}
      >
        {/* Landscape mode - full screen camera */}
        {isLandscapeMode ? (
          <div className={styles.landscapeWrapper}>
            <div
              className={`${styles.cameraContainer} ${styles.cameraContainerLandscape}`}
              onClick={handleTapToFocus}
            >
              <Webcam
                audio={false}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                videoConstraints={videoConstraints}
                forceScreenshotSourceSize
                className={`${styles.webcam} ${styles.webcamLandscape}`}
                playsInline
                onUserMedia={handleUserMedia}
                onUserMediaError={handleUserMediaError}
                screenshotQuality={1.0}
                mirrored={cameraSide === "user"}
              />

              {/* Close button */}
              <button className={styles.closeButton} onClick={handleModalClose}>
                <CloseIcon />
              </button>

              {/* Camera switch button */}
              {/* <button
                className={styles.cycleBackCameraButton}
                onClick={() => setCameraSide(cameraSide === "user" ? "environment" : "user")}
              >
                <AutorenewIcon />
              </button> */}

              {/* Cycle back cameras button - only show when using back camera and multiple cameras available */}
              {cameraSide === "environment" && backCameras.length > 1 && (
                <Button
                  className={styles.cameraSwitchButton}
                  onClick={cycleBackCamera}
                  startIcon={<FlipCameraAndroidIcon />}
                  sx={{
                    position: 'absolute',
                    top: '50px',
                    right: '20px',
                    zIndex: 5,
                  }}
                >
                  {Vocabulary.cycleCameras}
                </Button>
              )}

              {/* In-camera capture button */}
              {cameraReady && !cameraError && (
                <div className={styles.captureButton} onClick={takePicture}>
                  <CameraEnhanceIcon sx={{ fontSize: 36, color: "#4CAF50" }} />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div
            className={`${styles.portraitWrapper} ${
              !isMobileDevice ? styles.portraitWrapperDesktop : ""
            }`}
          >
            {/* Header */}
            <h2 className={styles.header}>{Vocabulary.takeAPhoto}</h2>

            {documentType === ScanDocumentType.ID && !isProcessingBack && (
              <FormControlLabel
                control={
                  <Switch
                    checked={isNewIdModel}
                    onChange={(e) => setIsNewIdModel(e.target.checked)}
                    color="primary"
                  />
                }
                label={Vocabulary.newIdCardModel}
                sx={{ mb: 1 }}
              />
            )}

            <Grid container spacing={isMobileDevice ? 2 : 3} sx={{ flex: 1 }}>
              {/* Camera Switch Button - Mobile Only */}
              {isMobileDevice && showCamera && (
                <Grid
                  item
                  xs={12}
                  display="flex"
                  justifyContent="center"
                  gap={2}
                >
                  <Button
                    startIcon={<AutorenewIcon />}
                    variant="outlined"
                    onClick={() =>
                      setCameraSide(
                        cameraSide === "user" ? "environment" : "user"
                      )
                    }
                    className={styles.button}
                  >
                    {Vocabulary.changeCamera}
                  </Button>

                  {/* Cycle back cameras button - only show when using back camera and multiple cameras available */}
                  {cameraSide === "environment" && backCameras.length > 1 && (
                    <Button
                      startIcon={<FlipCameraAndroidIcon />}
                      variant="outlined"
                      onClick={cycleBackCamera}
                      className={styles.button}
                    >
                      {Vocabulary.cycleCameras || "Cycle Cameras"}
                    </Button>
                  )}
                </Grid>
              )}

              {/* Camera View */}
              {showCamera && (
                <Grid item xs={12}>
                  <div
                    className={styles.cameraContainer}
                    onClick={handleTapToFocus}
                  >
                    <Webcam
                      audio={false}
                      ref={webcamRef}
                      screenshotFormat="image/jpeg"
                      videoConstraints={videoConstraints}
                      forceScreenshotSourceSize
                      className={`${styles.webcam} ${
                        isMobileDevice ? styles.webcamMobile : ""
                      }`}
                      playsInline
                      onUserMedia={handleUserMedia}
                      onUserMediaError={handleUserMediaError}
                      screenshotQuality={1.0}
                      mirrored={cameraSide === "user"}
                    />
                  </div>

                  {/* Instructions */}
                  {cameraReady && !cameraError && (
                    <div
                      className={`${styles.instructions} ${
                        styles.instructionsPortrait
                      } ${
                        documentType === ScanDocumentType.DRIVER_LICENSE ||
                        (documentType === ScanDocumentType.ID && isNewIdModel)
                          ? styles.instructionsDriverLicense
                          : ""
                      }`}
                    >
                      {documentType === ScanDocumentType.DRIVER_LICENSE
                        ? isProcessingBack
                          ? Vocabulary.fitYourDriverLicenseBackInSquare
                          : Vocabulary.fitYourDriverLicenseFrontInSquare
                        : documentType === ScanDocumentType.ID && isNewIdModel
                        ? isProcessingBack
                          ? Vocabulary.fitYourIdBackInSquare
                          : Vocabulary.fitYourIdFrontInSquare
                        : Vocabulary.fitYourImageInSquare}
                    </div>
                  )}

                
                </Grid>
              )}

              {/* Hidden File Input */}
              <input
                accept="image/*"
                className={styles.hiddenInput}
                id="upload-file"
                type="file"
                onChange={handleFileUpload}
              />

              {/* Image Preview */}
              {image && (
                <Grid item xs={12}>
                  <Paper elevation={2} className={styles.previewContainer}>
                    <h3 className={styles.previewTitle}>
                      {Vocabulary.photoTaken}
                    </h3>

                    <img
                      src={image || "/placeholder.svg"}
                      alt="Preview"
                      className={`${styles.previewImage} ${
                        isMobileDevice ? styles.previewImageMobile : ""
                      }`}
                    />

                    <div className={styles.previewButtonContainer}>
                      <Button
                        variant="outlined"
                        onClick={resetCamera}
                        startIcon={<RefreshIcon />}
                        className={styles.button}
                      >
                        {Vocabulary.cancel}
                      </Button>

                      <Button
                        variant="contained"
                        endIcon={<NavigateNextIcon />}
                        onClick={() => handleProcessImage(image)}
                        className={`${styles.button} ${styles.buttonPrimary}`}
                      >
                        {Vocabulary.process}
                      </Button>
                    </div>
                  </Paper>
                </Grid>
              )}

              {/* Action Buttons */}
              <Grid item xs={12}>
                <div className={styles.actionButtons}>
                  <Button
                    variant="outlined"
                    onClick={handleModalClose}
                    startIcon={<CloseIcon />}
                    className={styles.button}
                  >
                    {Vocabulary.close}
                  </Button>

                  {showCamera && (
                    <>
                      <Button
                        variant="contained"
                        startIcon={<CameraEnhanceIcon />}
                        onClick={takePicture}
                        className={`${styles.button} ${styles.buttonPrimary}`}
                      >
                        {Vocabulary.takePhoto}
                      </Button>

                      <Button
                        variant="outlined"
                        startIcon={<UploadFileIcon />}
                        onClick={() =>
                          document.getElementById("upload-file")?.click()
                        }
                        className={styles.button}
                      >
                        {Vocabulary.uploadPhoto}
                      </Button>
                    </>
                  )}
                </div>
              </Grid>
            </Grid>
          </div>
        )}
      </Dialog>

      {/* Processing Dialog */}
      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        PaperProps={{
          className: styles.processingDialog,
        }}
      >
        <Grid container direction="column" alignItems="center" spacing={3}>
          <Grid item>
            <div className={styles.processingIconContainer}>
              <CameraEnhanceIcon sx={{ fontSize: 32, color: "#009846" }} />
            </div>
          </Grid>

          <Grid item>
            <h3 className={styles.processingTitle}>
              {Vocabulary.imageIsProcessing}
            </h3>
            <p className={styles.processingSubtitle}>
              {Vocabulary.pleaseWaitProcessingYourPhoto}
            </p>

            <CircularProgress size={40} sx={{ color: "#009846" }} />
          </Grid>
        </Grid>
      </Dialog>
    </>
  );
}
